// DOM helpers. Third-party text only ever goes through text nodes; nothing
// here touches innerHTML.

export type Child = Node | string | number | null | undefined | false;

type Attrs = {
  class?: string;
  title?: string;
  style?: Partial<CSSStyleDeclaration>;
  onclick?: (e: MouseEvent) => void;
  [attr: string]: unknown;
};

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  attrs?: Attrs | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs ?? {})) {
    if (v === undefined || v === null || v === false) continue;
    if (k === "style") Object.assign(el.style, v);
    else if (k.startsWith("on") && typeof v === "function") {
      el.addEventListener(k.slice(2), v as EventListener);
    } else el.setAttribute(k === "class" ? "class" : k, String(v));
  }
  append(el, children);
  return el;
}

export function append(el: Node, children: Child[]): void {
  for (const c of children) {
    if (c === null || c === undefined || c === false) continue;
    el.appendChild(typeof c === "object" ? c : document.createTextNode(String(c)));
  }
}

export function clear(el: Node, ...children: Child[]): void {
  while (el.firstChild) el.removeChild(el.firstChild);
  append(el, children);
}

/// External link. Anything but http(s) becomes plain text.
export function link(href: unknown, text: Child, title?: string): HTMLElement {
  let ok = false;
  try {
    const u = new URL(String(href));
    ok = u.protocol === "https:" || u.protocol === "http:";
  } catch {
    ok = false;
  }
  if (!ok) return h("span", null, text);
  return h("a", { href: String(href), target: "_blank", rel: "noopener noreferrer", title }, text);
}

export function loading(text = "Loading"): HTMLElement {
  return h("div", { class: "loading" }, text);
}

export function errorBox(e: unknown): HTMLElement {
  const msg = e instanceof Error ? e.message : String(e);
  return h("div", { class: "err" }, msg);
}

export function kv(pairs: [string, Child][]): HTMLElement {
  const el = h("div", { class: "kv" });
  for (const [k, v] of pairs) {
    if (v === null || v === undefined || v === "" || v === "—") continue;
    el.append(h("span", { class: "k" }, k), h("span", { class: "v" }, v));
  }
  return el;
}

export function button(label: string, onclick: () => void, on = false, title?: string): HTMLButtonElement {
  return h("button", { class: on ? "on" : undefined, onclick, title }, label);
}

/// A row of mutually exclusive buttons.
export function toggles<T extends string>(
  options: readonly (T | readonly [T, string])[],
  current: T,
  onpick: (v: T) => void,
): HTMLElement[] {
  return options.map((o: T | readonly [T, string]) => {
    const [value, label] = typeof o === "string" ? [o, o] : o;
    return button(label, () => onpick(value), value === current);
  });
}

/// "ALL" plus one button per option, any number on at once. An empty
/// selection means all; selecting every option collapses back to empty.
export function multiToggles<T extends string>(
  options: readonly T[] | readonly [T, string][],
  selected: ReadonlySet<T>,
  onchange: (next: Set<T>) => void,
): HTMLElement[] {
  const all = options.map((o) => (Array.isArray(o) ? o : [o as T, o as string]) as [T, string]);
  const toggle = (v: T) => {
    const next = new Set(selected);
    if (!next.delete(v)) next.add(v);
    onchange(next.size === all.length ? new Set() : next);
  };
  return [
    button("ALL", () => onchange(new Set()), selected.size === 0),
    ...all.map(([value, label]) => button(label, () => toggle(value), selected.has(value))),
  ];
}

const SVGNS = "http://www.w3.org/2000/svg";

/// Sparkline of `values`, green or red by first vs last (or vs `base`).
export function spark(values: (number | null)[], width = 90, height = 16, base?: number): SVGElement {
  const svg = document.createElementNS(SVGNS, "svg");
  svg.setAttribute("class", "spark");
  svg.setAttribute("width", String(width));
  svg.setAttribute("height", String(height));
  const v = values.filter((x): x is number => typeof x === "number" && isFinite(x));
  if (v.length < 2) return svg;
  const ref = base ?? v[0];
  const lo = Math.min(...v, ref);
  const hi = Math.max(...v, ref);
  const span = hi - lo || 1;
  const y = (x: number) => (height - 1 - ((x - lo) / span) * (height - 2)).toFixed(1);
  const pts = v.map((x, i) => `${((i / (v.length - 1)) * (width - 1)).toFixed(1)},${y(x)}`);
  const baseLine = document.createElementNS(SVGNS, "line");
  baseLine.setAttribute("x1", "0");
  baseLine.setAttribute("x2", String(width));
  baseLine.setAttribute("y1", y(ref));
  baseLine.setAttribute("y2", y(ref));
  baseLine.setAttribute("stroke", "#444");
  baseLine.setAttribute("stroke-dasharray", "2 2");
  const line = document.createElementNS(SVGNS, "polyline");
  line.setAttribute("points", pts.join(" "));
  line.setAttribute("fill", "none");
  line.setAttribute("stroke", v[v.length - 1] >= ref ? "#3ddc5f" : "#ff4b4b");
  line.setAttribute("stroke-width", "1.2");
  svg.append(baseLine, line);
  return svg;
}

/// Position of `v` within [lo, hi] as a small bar (52-week range etc).
export function rangeBar(lo?: number, hi?: number, v?: number, width = 80): HTMLElement {
  const el = h("div", { class: "rangebar", style: { width: `${width}px` } });
  if (lo === undefined || hi === undefined || v === undefined || hi <= lo) return el;
  const pct = Math.max(0, Math.min(1, (v - lo) / (hi - lo)));
  el.append(h("i", { style: { left: `calc(${(pct * 100).toFixed(1)}% - 1px)` } }));
  return el;
}

/// Horizontal bar scaled to `max`, for in-table magnitudes.
export function magBar(v: number, max: number, width = 60): HTMLElement {
  const w = max > 0 ? Math.min(width, (Math.abs(v) / max) * width) : 0;
  return h("span", { class: `bar ${v < 0 ? "neg" : "pos"}`, style: { width: `${w.toFixed(0)}px` } });
}

export function download(filename: string, text: string, type = "text/csv"): void {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = h("a", { href: url, download: filename });
  document.body.append(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
